/**
 * THE APP'S ONE LAYER SCALE. Every surface that floats over a page (the
 * chrome, the notices, the toasts) and every layer that blocks one (dialogs,
 * sheets, gates, the confirm) takes its z-index from here, as a number a
 * test can compare (jsdom loads no CSS). CSS and Tailwind read the same
 * values as `--ee-z-*` custom properties (design-system/layers.css,
 * generated from this file; a class is `z-(--ee-z-chrome)`, never `z-50`).
 *
 * Low to high, app-wide:
 *
 *   in-page overlay 50 < chrome 50 < raised nav 99 < launcher 100 = banner 100
 *   < panel 101 < dialog 120 < raised dialog 130 < sheet 150 < prompt 160
 *   < ring 170 < gate 180 < (the onboarding tour, 200 to 300) = guest prompt 300
 *   < toast 400 < CONFIRM 500 < suspended 1000
 *
 * The rule the scale encodes: a surface that FLOATS (chrome, notices,
 * launchers, toasts) never covers one that asks a question, and a layer
 * raised FROM another sits above it (a sheet raises a gate, anything raises
 * a confirm).
 *
 * Phone pass P7-1: the group request sheet was `z-[150]` and the circle
 * agreement `z-50`. The agreement opened BEHIND the sheet, Send spun on the
 * parked request and the X was disabled: no way out. 2026-10-30: the shared
 * ConfirmModal was at 50 and the starting-soon card (100) painted over it
 * and took its taps; the bottom nav (50, later in the DOM) did the same.
 *
 * utils/__tests__/layer-scale-guard.test.ts fails on any raw z-index of 40
 * or more (a `z-40` / `z-[60]` class, with or without a `md:` prefix, an
 * inline `zIndex: 60`, a `z-index: 60` in a stylesheet) outside its frozen
 * list, and on a `var(--ee-z-*)` the scale does not define: new layers take
 * a constant from here.
 * utils/__tests__/layer-order.test.ts holds the order above.
 */

/**
 * The minimized queue / call pill and its expanded panel (QueueOverlay): the
 * way back to a call, under the chrome so it never covers the nav.
 */
export const Z_FLOAT = 40;

/**
 * An overlay that belongs to the page under it and must stay below that
 * page's own controls: the shared HelpTooltip and RoleSwitchOverlay (also
 * the Echo Arena's dialog tier, Z_ARENA_DIALOG). Not a dialog: a dialog is
 * Z_DIALOG.
 */
export const Z_IN_PAGE_OVERLAY = 50;

/**
 * The chrome: the app's top bar and bottom nav (dashboard/layout.tsx), the
 * public site's fixed headers, and the bars that ride above the nav (the
 * listening schedule's unsaved-changes bar).
 */
export const Z_CHROME = 50;

/** The bottom nav's raised Empathy button, which rises above the bar. */
export const Z_NAV_RAISED = 99;

/** The floating Empy and support launchers (design-system/launcher.ts). */
export const Z_LAUNCHER = 100;

/**
 * The circle notices at the top of the screen: the start banners, the
 * starting-soon card (CircleStartBanner, StartingSoonCard) and the ring's
 * availability notice. Above the chrome they sit under; below every dialog.
 */
export const Z_BANNER = 100;

/** The support-chat panel and its scrim. */
export const Z_PANEL = 101;

/**
 * A centred dialog raised from a PAGE (not from a sheet): ReportUserModal,
 * PreflightModal, the post-session flow and its feedback form, the cohort
 * dialogs, the out-of-credits family's base `.ee-prompt-modal`. Above every
 * surface that floats over a page (chrome, launchers, notices, the chat
 * panel), so nothing paints over it or takes its taps; below the sheets.
 */
export const Z_DIALOG = 120;

/**
 * A dialog raised FROM a dialog (not a confirm, which is Z_CONFIRM): the
 * wallet's setup card over the wallet sheet (WalletModal, at Z_DIALOG). It
 * was a raw `z-[60]` and opened UNDER the sheet once the sheet moved to the
 * dialog layer (#1159 review round 1). Below every sheet.
 */
export const Z_DIALOG_RAISED = 130;

/**
 * Full-screen sheets: RequestEmpathySheet, BookingSheet, PostCallPrompt.
 * Above every dialog a page can raise.
 */
export const Z_SHEET = 150;

/**
 * The shared out-of-credits prompts (OutOfTokensModal, OutOfVideoMinutesModal):
 * they open from a page, from Empy and from inside the support-chat panel,
 * so they sit above every sheet and panel. Below the ring and the gates: a
 * 402 / 403 gate the same request raised is on top of them (review round 1
 * of #1045, finding 1: the dialog opened UNDER the support panel and took
 * focus there).
 */
export const Z_PROMPT = 160;

/**
 * The in-app ring (P7d): "Someone needs empathy now" · Accept / Not now.
 * Above every sheet (a ring is time-sensitive and app-wide), BELOW the
 * gates: Accept starts a call through the gated routes, so the agreement or
 * norms gate it can raise must sit on top of it (the #988 order), and the
 * ring waits behind a gate that is already up (ListenerRingHost).
 */
export const Z_RING = 170;

/**
 * What the API interceptor raises over whatever made the request: the
 * circle agreement, the empathy norms, the upgrade prompt (and the plans
 * it opens). Above every sheet; below the tour and the suspended screen.
 */
export const Z_GATE = 180;

/**
 * The top of the onboarding tour's band. The tour's own layers (the
 * spotlight 200, the raised targets 201, the speech bubble 205, the hints'
 * arrow 210, the intro overlay 300) live in components/onboarding; this is
 * the line every layer above the tour is measured against.
 */
export const Z_TOUR_TOP = 300;

/**
 * The guest's sign-up prompt (GuestUpgradeModal): a gated ACTION a guest
 * takes, often from a guest-tour step, so it opens at the top of the tour's
 * band, over its spotlight (it was a raw `z-[300]`, the same value). Below
 * the toasts and the confirm.
 */
export const Z_GUEST_PROMPT = Z_TOUR_TOP;

/**
 * The toasts (react-toastify's container, design-system/toasts.css). Above
 * the tour (a tour step moves an alert to the bottom edge, over the hints
 * bar) and every sheet and gate, so a request's error shows wherever it was
 * made from. Below the confirm: a toast never sits on a question.
 */
export const Z_TOAST = 400;

/**
 * THE CONFIRM: the shared ConfirmModal's default. Above everything that
 * floats (chrome, notices, launchers, panel, toasts, the tour) and above
 * every layer it can be raised FROM (a dialog, a sheet, a prompt, the ring,
 * a gate), so "Discard this conversation?" over the Giraffe dialog and
 * "Block?" over a page need no layer of their own. A confirm closes before
 * its action runs, so a gate that action raises never sits under it.
 */
export const Z_CONFIRM = 500;

/** The suspended-account screen: over everything, the confirm included. */
export const Z_SUSPENDED = 1000;

/*
 * ECHO ARENA'S OWN LAYERS (review round 1 of #1040). The arena root
 * (EchoArena.tsx) is `fixed inset-0 z-10`, a stacking context, so these
 * order the arena's layers among themselves and never meet the app-wide
 * values above. CSS reads every constant in this file as a `--ee-z-*`
 * custom property (design-system/layers.css, kept equal to this file by
 * utils/__tests__/layers-css.test.ts); pages/echo-arena.css uses nothing
 * else for a z-index.
 */

/** Inside a panel that is its own stacking context: the recall sheet's sticky head. */
export const Z_ARENA_RAISED = 1;

/** The mode label's explainer, over the stage beside it. */
export const Z_ARENA_POPOVER = 20;

/** The thinking dots under the portrait while a turn is scored. */
export const Z_ARENA_FLOAT = 30;

/** Paused, and roleplay's "I didn't hear you". */
export const Z_ARENA_PAUSE = 40;

/**
 * The intro and the dialogs (the character page, the unlock card). The
 * dialog tier itself, so the shared HelpTooltip and RoleSwitchOverlay
 * (Z_IN_PAGE_OVERLAY, also rendered by the AI Buddy outside the arena) take the same
 * slot inside it.
 */
export const Z_ARENA_DIALOG = Z_IN_PAGE_OVERLAY;

/** The anger eruption's red wash (pointer-events: none). */
export const Z_ARENA_ERUPTION = 52;

/** The recall sheet's dimmer, over the stage and every dialog. */
export const Z_ARENA_SHEET_SCRIM = 54;

/** The recall sheet. */
export const Z_ARENA_SHEET = 56;

/** "Setting the scene...": the start's loading overlay, over the character dialog that started it. */
export const Z_ARENA_BUSY = 58;

/**
 * The session's corner controls (Quit, Pause, My notes, the typing toggle):
 * above every overlay, the recall sheet included, so Quit is always one tap
 * away (a role-switch overlay's exit animation used to swallow taps).
 */
export const Z_ARENA_CORNER = 60;
